import { omit } from 'lodash';

import { JobModel, ListResponseModel, QueryParamModel, WorkerViewModel } from 'models';
import { SendMessageFormProps } from 'pages/Kozos/Workers/SendMessageDialog';
import request, { Methods, requestFile } from 'utils/request';
import { EntityService } from 'utils/entityService';
import { exportFile } from 'utils/exportFile';
import { Mock } from 'utils/mockDecorator';
import { mockListResponse } from 'mock/list';

class WorkersService extends EntityService {
  constructor() {
    super('worker');
  }

  async sendMessage(data: SendMessageFormProps) {
    return request<WorkerViewModel>({
      method: Methods.POST,
      resource: 'message/send',
      data,
    });
  }

  async syncWorkers() {
    return request<{ jobId: string }>({
      method: Methods.POST,
      resource: 'admin/worker-sync',
    });
  }

  async getSyncTaskById(jobId: ID) {
    return request<JobModel>({
      method: Methods.GET,
      resource: `admin/worker-sync/jobs/${jobId}`,
    });
  }

  @Mock(() => import('mock/workers').then((m) => m.MOCK_LATEST_SYNC_JOB))
  async getLatestSyncJob() {
    return request<JobModel>({
      method: Methods.GET,
      resource: 'admin/worker-sync/jobs/latest',
    });
  }

  @Mock((params: QueryParamModel) =>
    import('mock/workers').then((m) => mockListResponse(m.MOCK_WORKERS, params))
  )
  async getWorkersList(params?: QueryParamModel | null) {
    return request<ListResponseModel<WorkerViewModel>>({
      method: Methods.GET,
      resource: this.resource,
      params,
    });
  }

  @Mock(() => import('mock/workers').then((m) => m.MOCK_WORKERS))
  async searchWorkers(params?: QueryParamModel) {
    return request<WorkerViewModel[]>({
      method: Methods.GET,
      resource: `${this.resource}/search`,
      params,
    });
  }

  async editWorker(id: ID, data: Partial<WorkerViewModel>) {
    const updatedData = omit(data, ['id', 'isActive']);

    return request<WorkerViewModel>({
      method: Methods.PATCH,
      resource: `${this.resource}/${id}`,
      data: updatedData,
    });
  }

  async exportWorkers(params?: QueryParamModel | null) {
    const response = await requestFile<BlobPart>({
      method: Methods.GET,
      resource: `${this.resource}/export`,
      params,
    });

    exportFile(response);
    return response;
  }

  async deleteWorker(id: ID) {
    return request<void>({
      method: Methods.DELETE,
      resource: `${this.resource}/${id}`,
    });
  }
}

export const workersService = new WorkersService();
